import AnchorDialog from '@base/components/src/AnchorDialog';
import Radio from '@base/components/src/Radio';
import { RadioComponentProps } from '@base/components/src/Radio/types';
import Spinner from '@base/components/src/Spinner';
import { MouseEventHandler, useEffect, useRef } from 'react';

import MissingItemsModal from '../MissingItemModal';

import styles from './Service.module.css';

type DefaultOptionValue = string | number | null;

type ServiceInfoIconProps = {
  productSku: string;
  serviceId: number;
  addService(): Promise<void>;
};

function ServiceInfoIconWithModal(props: ServiceInfoIconProps) {
  const { productSku, serviceId, addService } = props;

  return (
    <AnchorDialog
      contentClassName={styles.serviceModal}
      dialogHeaderClassName={styles.serviceRadioDialogHeader}
      anchorContent={(open: () => void) => (
        <img
          data-testid="servic-dialog-trigger"
          src="/marketplace-static/images/icons/infoIcon.svg"
          alt="info icon"
          width={14}
          height={14}
          className={`${styles.infoIcon} infoIcon`}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            open();
          }}
        />
      )}
    >
      {(_: any, close: () => void) => (
        <MissingItemsModal
          infoMode
          productSku={productSku}
          serviceId={serviceId}
          addService={addService}
          closeModal={close}
        />
      )}
    </AnchorDialog>
  );
}

type ServiceRadioProps = {
  selectedService: DefaultOptionValue;
  productSku: string;
  serviceId: number;
  canAddToCart: boolean;
  stopLoading(): void;
  addService(): Promise<void>;
  hideInfoIcon?: boolean;
} & RadioComponentProps;

export default function ServiceRadio(props: ServiceRadioProps) {
  const {
    onClick,
    selectedService,
    productSku,
    serviceId,
    canAddToCart,
    stopLoading,
    addService,
    hideInfoIcon = true,
    ...rest
  } = props;
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const showLoading = selectedService === rest.value;

  useEffect(() => {
    if (props.checked) {
      stopLoading();
    }
    return () => {
      stopLoading();
    };
  }, [props.checked]);

  useEffect(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    if (showLoading) {
      timeout.current = setTimeout(() => {
        stopLoading();
      }, 5000);
    }
  }, [props.checked, showLoading]);

  return canAddToCart ? (
    <Radio
      {...rest}
      onClick={onClick}
      labelClassName={`${styles.label} serviceRadioLabel`}
      radioClassName={styles.serviceRadioIcon}
      size="small"
      endAdornmentClassName={styles.radioIcon}
      endAdornment={showLoading && <Spinner className={styles.serviceRadioSpinner} />}
      textEndAdornmentClassName={'ml-2'}
      textEndAdornment={
        !hideInfoIcon && (
          <ServiceInfoIconWithModal productSku={productSku} serviceId={serviceId} addService={addService} />
        )
      }
    />
  ) : (
    <AnchorDialog
      contentClassName={styles.serviceModal}
      dialogHeaderClassName={styles.serviceRadioDialogHeader}
      anchorContent={(open: MouseEventHandler<HTMLSpanElement> | undefined) => (
        <span data-testid={'service-radio-dialog-trigger'} onClick={open}>
          <Radio
            {...rest}
            labelClassName={`${styles.label} serviceRadioLabel`}
            radioClassName={styles.serviceRadioIcon}
            endAdornmentClassName={styles.radioIcon}
            size="small"
            endAdornment={showLoading && <Spinner className={styles.serviceRadioSpinner} />}
            textEndAdornmentClassName={'ml-2 flex-[1_1_10%]'}
            textEndAdornment={
              <img
                src="/marketplace-static/images/icons/infoIcon.svg"
                alt="info icon"
                width={14}
                height={14}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  open && open(e);
                }}
                className={`${styles.infoIcon} infoIcon`}
              />
            }
          />
        </span>
      )}
    >
      {(_: any, close: () => void) => (
        <MissingItemsModal productSku={productSku} serviceId={serviceId} addService={addService} closeModal={close} />
      )}
    </AnchorDialog>
  );
}
